import { all, call, put, select, take, takeEvery, takeLatest } from "redux-saga/effects";
import { generatePath } from "react-router-dom";
import { mergeWith } from "lodash";
import { saveAs } from "file-saver";
import BackendClient from "api/backend-client";
import { ERROR_CODE_NOT_FOUND, ErrorType } from "api/api-error";
import { RecipeScalingApiMethods } from "api/api-methods";
import WSService from "api/web-socket.service";
import { rsSocketUrl } from "api/url-builder";
import { getCallbacks, InvokeRsSocketMethods } from "api/rs-socket-callbacks";
import logger from "utils/logger";
import { GlobalEntityType } from "shared/types";
import { waitForLogin } from "../auth/auth.saga";
import { cancelFailure, cancelSuccessful, clearDependentWorkspaces, createFailure, createSuccessful, duplicateFailure, duplicateSuccessful, getDependentWorkspaces, getDependentWorkspacesFailure, getDependentWorkspacesSuccessful, getRestrictionsFailure, getRestrictionsSuccessful, getUpdatedOwnershipInfoFailure, getUpdatedOwnershipInfoSuccessful, KnowledgeBaseItemActionType, linkTagsFinish, recoverFailure, recoverSuccessful, reset, revokeOwnershipFailure, revokeOwnershipSuccessful, save, saveFailure, saveSuccessful, setCurrentRequestTemplate, setDependentCallback, setDependentDialogOpened, setEditableFailure, setEditableSuccessful, setServerError, setSocketStarted, shareOwnershipFailure, shareOwnershipSuccessful, socketErrorMessage, unlinkTagsFinish, updateDependentWorkspacesFailure, updateDependentWorkspacesSuccessful, updateFailure, updateSuccessful, updateTags, } from "./knowledge-base-item.actions";
import { currentItemIdentifierSelector, dependentCallbackSelector, entityTypeSelector, getKnowledgeBaseItemState, isEditableSelector, isSocketStartedSelector, preventNavigationSelector, } from "./knowledge-base-item.selectors";
import { RequestTemplate, } from "./knowledge-base-item.types";
import RoutePaths from "../../routes/route-paths";
import { goHome, setDetailsKnowledgeItemToView, setOffline, setRsEntityCreatingModalOpen, } from "../global/global.actions";
import { isNotEmpty } from "../../utils/validate";
import { recovery as cellModelRecovery } from "../cell-model/cell-model.saga";
import { recovery as recipeRecovery } from "../recipe/recipe.saga";
import { recovery as workspaceRecovery } from "../rs-workspace/sagas/rs-workspace.saga";
import { preloadWorkspacePreviews, rsWorkspaceAddCellModel, rsWorkspaceAddProcessData, rsWorkspaceAddRecipe, setLockedDialogData, } from "store/rs-workspace/actions/rs-workspace.actions";
import SocketChannel from "store/socket-channel";
import { getCurrentDateTimeStamp } from "utils/dateTime";
import { setSeats, changeSeatToastVisibleStatus } from "store/licensing/licensing.actions";
import { SeatsToastVisibleStatusType } from "store/licensing/licensing.types";
import { checkServerAvailable } from "utils/network";
import { getResultSetsSelector } from "store/rs-workspace/selectors/results.selectors";
import { getErrorByType } from "./knowledge-base-item.utils";
import { getAllSimulationResultsSaga } from "store/rs-workspace/sagas/results.saga";
import { currentLocationSelector } from "store/router/router.selectors";
import { isProcessDataUploadPage } from "routes";
import { noSeatsAvailableSelector } from "store/licensing/licensing.selectors";
import { checkForbidden } from "store/auth/auth.saga";
import { detailsKnowledgeItemToViewSelector } from "store/global/global.selectors";
import { apiErrorHandler } from "store/global/global.saga";
import { isSubscribedSelector } from "store/ps-workspace/ps-workspace.selectors";
import { navigate } from "shared/navigation";
import { ROUTE_CHANGED } from "store/router/router.actions";
export function* connectChannelSaga() {
    try {
        const isEditable = yield select(isEditableSelector);
        const { entityType } = yield select(currentItemIdentifierSelector);
        if (!isEditable &&
            entityType !== GlobalEntityType.RsWorkspace &&
            entityType !== GlobalEntityType.CellModel &&
            entityType !== GlobalEntityType.ProcessData) {
            return;
        }
        yield call([WSService.getInstance(), "init"], rsSocketUrl);
        yield call([SocketChannel.getInstance(), "createSocketChannel"], WSService.getInstance(), getCallbacks);
        yield call([WSService.getInstance(), "start"]);
        yield put(setSocketStarted(true));
        const noSeatsAvailable = yield select(noSeatsAvailableSelector);
        if ((isEditable || entityType === GlobalEntityType.ProcessData) && !noSeatsAvailable) {
            yield call(subscribeSaga);
        }
        while (true) {
            const action = yield take(SocketChannel.getInstance().getChannel());
            yield put(action);
        }
    }
    catch (error) {
        logger.error(error);
    }
}
export function* disconnectChannelSaga() {
    try {
        const { pathname } = yield select(currentLocationSelector);
        const isNavigationPrevented = yield select(preventNavigationSelector);
        const { id, entityType } = yield select(getKnowledgeBaseItemState);
        const isPsWorkspaceSubscribed = yield select(isSubscribedSelector);
        if (isPsWorkspaceSubscribed) {
            yield call([WSService.getInstance(), "stop"]);
            return;
        }
        if (id === null || entityType === null || isNavigationPrevented) {
            const detailsKnowledgeItemToView = yield select(detailsKnowledgeItemToViewSelector);
            if (detailsKnowledgeItemToView) {
                yield put(setDetailsKnowledgeItemToView(undefined));
            }
            return;
        }
        const isSocketStarted = yield select(isSocketStartedSelector);
        if (isSocketStarted) {
            if (isProcessDataUploadPage(pathname)) {
                return;
            }
            yield call([WSService.getInstance(), "stop"]);
            yield put(setSocketStarted(false));
        }
        yield put(reset());
    }
    catch (error) {
        logger.error(error);
    }
}
function* subscribeSaga() {
    try {
        const { id: elementId, entityType: elementType } = yield select(currentItemIdentifierSelector);
        yield call([WSService.getInstance(), "send"], InvokeRsSocketMethods.SUBSCRIBE, {
            elementId,
            elementType: elementType === GlobalEntityType.RsWorkspace ? "Workspace" : elementType,
        });
    }
    catch (error) {
        logger.error(error);
    }
}
function* reconnectSaga() {
    try {
        yield put(socketErrorMessage(null));
        yield put(setSocketStarted(true));
        yield call(subscribeSaga);
    }
    catch (error) {
        logger.error(error);
    }
}
function* handleSubscribedMessage({ payload }) {
    try {
        yield put(setEditableSuccessful());
        yield put(setSeats(payload.seats));
        yield put(changeSeatToastVisibleStatus(SeatsToastVisibleStatusType.OCCUPIED));
        yield put(setOffline(false));
    }
    catch (error) {
        logger.error(error);
        yield put(setEditableFailure());
    }
}
function* updateTagsSaga(id) {
    yield call(waitForLogin);
    const entityType = yield select(entityTypeSelector);
    let method = undefined;
    switch (entityType) {
        case GlobalEntityType.CellModel:
            method = RecipeScalingApiMethods.GetCellModel;
            break;
        case GlobalEntityType.Recipe:
            method = RecipeScalingApiMethods.GetRecipe;
            break;
        case GlobalEntityType.RsWorkspace:
            method = RecipeScalingApiMethods.GetRsWorkspace;
            break;
    }
    if (method) {
        const backendClient = BackendClient.getInstance();
        const { tags = [] } = yield call([backendClient, method], id);
        yield put(updateTags(tags));
    }
}
export function* linkTagsSaga({ payload }) {
    try {
        yield call(waitForLogin);
        const backendClient = BackendClient.getInstance();
        let tags = [];
        const existingTags = payload.tags.filter((tag) => tag.value !== undefined).map((tag) => tag.value);
        const tagsToCreate = payload.tags.filter((tag) => tag.value === undefined).map((tag) => tag.label);
        if (tagsToCreate.length > 0) {
            const tagsData = yield all(tagsToCreate.map((tag) => call([backendClient, RecipeScalingApiMethods.CreateRsTag], tag)));
            tags = [...existingTags, ...tagsData.map((tag) => tag.id)].filter((id) => id !== undefined);
        }
        else {
            tags = existingTags;
        }
        yield call([backendClient, RecipeScalingApiMethods.LinkRsTags], payload.entityType, {
            entityId: payload.entityId,
            tagIds: tags,
        });
        yield call(updateTagsSaga, payload.entityId);
    }
    catch (e) {
        yield call(apiErrorHandler, e, "link tags failed");
    }
    finally {
        yield put(linkTagsFinish());
    }
}
export function* unlinkTagsSaga({ payload }) {
    try {
        yield call(waitForLogin);
        const backendClient = BackendClient.getInstance();
        yield call([backendClient, RecipeScalingApiMethods.UnlinkRsTags], payload.entityType, payload);
        if (payload.entityId !== undefined) {
            yield call(updateTagsSaga, payload.entityId);
        }
    }
    catch (e) {
        yield call(apiErrorHandler, e, "unlink tags failed");
    }
    finally {
        yield put(unlinkTagsFinish());
    }
}
function* createSaga({ payload }) {
    try {
        yield call(waitForLogin);
        let method = undefined;
        let routePath;
        switch (payload.entityType) {
            case GlobalEntityType.CellModel:
                yield put(setCurrentRequestTemplate(RequestTemplate.CELL_MODEL_POST));
                method = RecipeScalingApiMethods.CreateCellModel;
                routePath = RoutePaths.EditCellModel;
                break;
            case GlobalEntityType.Recipe:
                yield put(setCurrentRequestTemplate(RequestTemplate.RECIPE_POST));
                method = RecipeScalingApiMethods.CreateRecipe;
                routePath = RoutePaths.EditRecipe;
                break;
            case GlobalEntityType.RsWorkspace:
                yield put(setCurrentRequestTemplate(RequestTemplate.WORKSPACE_POST));
                method = RecipeScalingApiMethods.CreateWorkspace;
                routePath = RoutePaths.EditRecipeScalingWorkspace;
                break;
        }
        if (method && routePath) {
            const backendClient = BackendClient.getInstance();
            const id = yield call([backendClient, method], payload);
            yield put(createSuccessful());
            yield put(setRsEntityCreatingModalOpen(false));
            yield call(navigate, generatePath(routePath, { id }));
        }
    }
    catch (e) {
        yield put(createFailure());
        yield call(apiErrorHandler, e, `${payload.entityType} creation is failed`);
    }
}
function* updateSaga({ payload }) {
    try {
        yield call(waitForLogin);
        let method;
        let currentRequestTemplate;
        switch (payload.entityType) {
            case GlobalEntityType.CellModel:
                currentRequestTemplate = RequestTemplate.CELL_MODEL_PUT;
                method = RecipeScalingApiMethods.UpdateCellModel;
                break;
            case GlobalEntityType.Recipe:
                currentRequestTemplate = RequestTemplate.RECIPE_PUT;
                method = RecipeScalingApiMethods.UpdateRecipe;
                break;
            case GlobalEntityType.RsWorkspace:
                currentRequestTemplate = RequestTemplate.WORKSPACE_PUT;
                method = RecipeScalingApiMethods.UpdateRsWorkspace;
                break;
            case GlobalEntityType.ProcessData:
                currentRequestTemplate = RequestTemplate.PROCESS_DATA_PUT;
                method = RecipeScalingApiMethods.UpdateProcessData;
                break;
        }
        if (method && currentRequestTemplate) {
            yield put(setCurrentRequestTemplate(currentRequestTemplate));
            const backendClient = BackendClient.getInstance();
            const { id, ...rest } = payload;
            yield call([backendClient, method], `${id}`, rest);
            yield put(updateSuccessful(payload));
            const detailsKnowledgeBaseItemToView = yield select(detailsKnowledgeItemToViewSelector);
            if (detailsKnowledgeBaseItemToView) {
                const { name, description } = payload;
                yield put(setDetailsKnowledgeItemToView({ ...detailsKnowledgeBaseItemToView, name, description }));
            }
        }
    }
    catch (e) {
        yield call(apiErrorHandler, e, "update is failed");
        yield put(updateFailure());
    }
}
function* saveSaga({ payload: { payload, callback } }) {
    try {
        const entityType = yield select(entityTypeSelector);
        let method;
        switch (entityType) {
            case GlobalEntityType.CellModel:
                method = RecipeScalingApiMethods.SaveCellModel;
                break;
            case GlobalEntityType.Recipe:
                method = RecipeScalingApiMethods.SaveRecipe;
                break;
            case GlobalEntityType.RsWorkspace:
                method = RecipeScalingApiMethods.SaveRsWorkspace;
                break;
        }
        if (method) {
            const backendClient = BackendClient.getInstance();
            const { value } = yield call([backendClient, method], payload);
            if (callback) {
                yield call(callback);
            }
            yield put(saveSuccessful(value));
        }
    }
    catch (e) {
        yield call(apiErrorHandler, e, "save is failed");
        yield put(saveFailure());
    }
}
function* cancelSaga({ payload: { payload, callback } }) {
    try {
        const entityType = yield select(entityTypeSelector);
        const backendClient = BackendClient.getInstance();
        switch (entityType) {
            case GlobalEntityType.CellModel:
                yield call([backendClient, RecipeScalingApiMethods.CancelCellModel], payload);
                break;
            case GlobalEntityType.Recipe:
                yield call([backendClient, RecipeScalingApiMethods.CancelRecipe], payload);
                break;
            case GlobalEntityType.RsWorkspace: {
                yield call([backendClient, RecipeScalingApiMethods.CancelRsWorkspace], payload);
                const resultSets = yield select(getResultSetsSelector);
                yield call(getAllSimulationResultsSaga, payload, resultSets);
                break;
            }
        }
        if (callback) {
            yield call(callback);
        }
        yield put(cancelSuccessful());
        if (entityType === GlobalEntityType.RsWorkspace) {
            yield put(preloadWorkspacePreviews());
        }
    }
    catch (e) {
        yield call(apiErrorHandler, e, "cancellation failed");
        yield put(cancelFailure());
    }
}
function* recoverSaga({ payload }) {
    try {
        const entityType = yield select(entityTypeSelector);
        switch (entityType) {
            case GlobalEntityType.CellModel:
                yield call(cellModelRecovery, payload);
                break;
            case GlobalEntityType.Recipe:
                yield call(recipeRecovery, payload);
                break;
            case GlobalEntityType.RsWorkspace: {
                yield call(workspaceRecovery, payload);
                const resultSets = yield select(getResultSetsSelector);
                yield call(getAllSimulationResultsSaga, payload, resultSets);
                break;
            }
        }
        yield put(recoverSuccessful());
        if (entityType === GlobalEntityType.RsWorkspace) {
            yield put(preloadWorkspacePreviews());
        }
    }
    catch (e) {
        yield call(apiErrorHandler, e, "recovery failed");
        yield put(recoverFailure());
    }
}
export function* loadItemErrorHandler(e) {
    yield call(checkForbidden, e);
    const isOnline = yield call(checkServerAvailable);
    if (!isOnline) {
        yield put(setOffline(true));
    }
    if (e.status === ERROR_CODE_NOT_FOUND) {
        yield put(goHome());
    }
}
function* getRestrictionsSaga({ payload }) {
    try {
        yield call(waitForLogin);
        const backendClient = BackendClient.getInstance();
        const payloads = Array.isArray(payload) ? payload : [payload];
        const restrictions = yield all(payloads.map((item) => call([backendClient, RecipeScalingApiMethods.GetRestrictions], item)));
        const preparedRestrictions = payloads.reduce((obj, { rsEndpoint, method, template }, index) => {
            const restriction = {
                [rsEndpoint]: {
                    [template]: {
                        [method]: {
                            ...(isNotEmpty(restrictions[index], true) ? restrictions[index] : { hasNoRestrictions: true }),
                        },
                    },
                },
            };
            return mergeWith(obj, restriction);
        }, {});
        yield put(getRestrictionsSuccessful(preparedRestrictions));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "load restrictions failed");
        yield put(getRestrictionsFailure());
    }
}
function* duplicateSaga({ payload: { payload, entityType, workspaceId } }) {
    try {
        yield call(waitForLogin);
        let method = undefined;
        let routePath;
        switch (entityType) {
            case GlobalEntityType.CellModel:
                yield put(setCurrentRequestTemplate(RequestTemplate.CELL_MODEL_POST));
                method = RecipeScalingApiMethods.DuplicateCellModel;
                routePath = RoutePaths.EditCellModel;
                break;
            case GlobalEntityType.Recipe:
                yield put(setCurrentRequestTemplate(RequestTemplate.RECIPE_POST));
                method = RecipeScalingApiMethods.DuplicateRecipe;
                routePath = RoutePaths.EditRecipe;
                break;
            case GlobalEntityType.ProcessData:
                yield put(setCurrentRequestTemplate(RequestTemplate.PROCESS_DATA_POST));
                method = RecipeScalingApiMethods.DuplicateProcessData;
                break;
            case GlobalEntityType.RsWorkspace:
                yield put(setCurrentRequestTemplate(RequestTemplate.WORKSPACE_POST));
                method = RecipeScalingApiMethods.DuplicateWorkspace;
                routePath = RoutePaths.EditRecipeScalingWorkspace;
                break;
        }
        if (method) {
            const backendClient = BackendClient.getInstance();
            const id = yield call([backendClient, method], payload);
            yield put(duplicateSuccessful());
            yield put(setRsEntityCreatingModalOpen(false));
            if (workspaceId === undefined) {
                yield call(disconnectChannelSaga);
                if (routePath) {
                    yield call(navigate, generatePath(routePath, { id }));
                }
            }
            else {
                switch (entityType) {
                    case GlobalEntityType.CellModel:
                        yield put(rsWorkspaceAddCellModel({ workspaceId, cellModelId: id }));
                        break;
                    case GlobalEntityType.Recipe:
                        yield put(rsWorkspaceAddRecipe({ workspaceId, recipeId: id }));
                        break;
                    case GlobalEntityType.ProcessData:
                        yield put(rsWorkspaceAddProcessData({ workspaceId, processDataId: id }));
                        break;
                }
                yield put(setLockedDialogData(null));
            }
        }
    }
    catch (e) {
        yield call(apiErrorHandler, e, "duplicate failed");
        yield put(duplicateFailure());
    }
}
function* exportSaga({ payload }) {
    try {
        const { saved, entityName, entityType, id } = payload;
        const backendClient = BackendClient.getInstance();
        let method = undefined;
        switch (entityType) {
            case GlobalEntityType.CellModel:
                method = RecipeScalingApiMethods.ExportCellModel;
                break;
            case GlobalEntityType.Recipe:
                method = RecipeScalingApiMethods.ExportRecipe;
                break;
            case GlobalEntityType.RsWorkspace:
                method = RecipeScalingApiMethods.ExportRsWorkspace;
                break;
        }
        if (!saved) {
            yield put(save({ payload: id }));
        }
        if (!method) {
            return;
        }
        const exportedData = yield call([backendClient, method], id);
        if (!exportedData) {
            throw new Error();
        }
        if (exportedData instanceof Blob) {
            saveAs(exportedData, `${getCurrentDateTimeStamp()}_${entityName}.${exportedData.type.split("/").pop()}`);
        }
        else {
            const json = JSON.stringify(exportedData);
            const fileToSave = new Blob([json], { type: "json" });
            saveAs(fileToSave, `${getCurrentDateTimeStamp()}_${entityName}.json`);
        }
    }
    catch (e) {
        logger.error("knowledge base item export failed");
    }
}
function* getDependentWorkspacesSaga({ payload, }) {
    try {
        const backendClient = BackendClient.getInstance();
        let method = undefined;
        switch (payload.entityType) {
            case GlobalEntityType.CellModel:
                method = RecipeScalingApiMethods.GetCellModelDependentWorkspaces;
                break;
            case GlobalEntityType.Recipe:
                method = RecipeScalingApiMethods.GetRecipeDependentWorkspaces;
                break;
            case GlobalEntityType.ProcessData:
                method = RecipeScalingApiMethods.GetProcessDataDependentWorkspaces;
                break;
        }
        if (method) {
            const data = yield call([backendClient, method], payload.entityId);
            if (data.length > 0) {
                yield put(setDependentCallback(payload.callback));
            }
            else if (payload) {
                !!payload.callback && payload.callback();
            }
            yield put(getDependentWorkspacesSuccessful(data.length ? data : undefined));
            return data;
        }
    }
    catch (e) {
        yield call(apiErrorHandler, e, "load dependent workspaces is failed");
        yield put(getDependentWorkspacesFailure());
    }
}
function* updateDependentWorkspacesSaga({ payload }) {
    const { entityType } = yield select(currentItemIdentifierSelector);
    try {
        const backendClient = BackendClient.getInstance();
        const dependentCallback = yield select(dependentCallbackSelector);
        let method = undefined;
        let routePath;
        switch (entityType) {
            case GlobalEntityType.CellModel:
                method = RecipeScalingApiMethods.UpdateCellModelDependentWorkspaces;
                routePath = RoutePaths.EditCellModel;
                yield put(setCurrentRequestTemplate(RequestTemplate.DEPENDENT_WORKSPACES_PUT));
                break;
            case GlobalEntityType.Recipe:
                method = RecipeScalingApiMethods.UpdateRecipeDependentWorkspaces;
                routePath = RoutePaths.EditRecipe;
                yield put(setCurrentRequestTemplate(RequestTemplate.DEPENDENT_WORKSPACES_PUT));
                break;
        }
        if (method) {
            const state = yield call([backendClient, method], payload);
            yield put(clearDependentWorkspaces());
            yield put(setServerError(null));
            yield put(setDependentDialogOpened(false));
            if (payload.entityId === state.new.id) {
                yield put(saveSuccessful(state.new.lastSavedDate));
                if (dependentCallback) {
                    dependentCallback();
                }
            }
            else if (routePath) {
                yield put(cancelSuccessful());
                yield call(navigate, generatePath(routePath, { id: state.new.id }));
            }
            yield put(setDependentCallback(undefined));
            yield put(updateDependentWorkspacesSuccessful());
        }
    }
    catch (e) {
        yield call(apiErrorHandler, e, "update dependent workspaces is failed");
        const { data: { errors = [] } = {} } = e;
        if (getErrorByType(ErrorType.DependentWorkspacesChanged, errors)) {
            yield put(getDependentWorkspaces({ entityType, entityId: payload.entityId }));
        }
        yield put(updateDependentWorkspacesFailure());
    }
}
function* shareOwnershipSaga({ payload }) {
    try {
        const backendClient = BackendClient.getInstance();
        let method = undefined;
        switch (payload.entityType) {
            case GlobalEntityType.CellModel:
                method = RecipeScalingApiMethods.ShareCellModelOwnership;
                break;
            case GlobalEntityType.Recipe:
                method = RecipeScalingApiMethods.ShareRecipeOwnership;
                break;
            case GlobalEntityType.RsWorkspace:
                method = RecipeScalingApiMethods.ShareWorkspaceOwnership;
                break;
            case GlobalEntityType.ProcessData:
                method = RecipeScalingApiMethods.ShareProcessDataOwnership;
                break;
        }
        if (!method) {
            return;
        }
        const knowledgeBaseItem = yield call([backendClient, method], payload);
        yield put(shareOwnershipSuccessful({
            entityType: payload.entityType,
            knowledgeBaseItem,
        }));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "share knowledge base item ownership failed");
        yield put(shareOwnershipFailure());
    }
}
function* revokeOwnershipSaga({ payload }) {
    try {
        const backendClient = BackendClient.getInstance();
        let method = undefined;
        switch (payload.entityType) {
            case GlobalEntityType.CellModel:
                method = RecipeScalingApiMethods.RevokeCellModelOwnership;
                break;
            case GlobalEntityType.Recipe:
                method = RecipeScalingApiMethods.RevokeRecipeOwnership;
                break;
            case GlobalEntityType.RsWorkspace:
                method = RecipeScalingApiMethods.RevokeWorkspaceOwnership;
                break;
            case GlobalEntityType.ProcessData:
                method = RecipeScalingApiMethods.RevokeProcessDataOwnership;
                break;
        }
        if (!method) {
            return;
        }
        const knowledgeBaseItem = yield call([backendClient, method], payload);
        yield put(revokeOwnershipSuccessful({
            entityType: payload.entityType,
            knowledgeBaseItem,
        }));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "revoke knowledge base item ownership failed");
        yield put(revokeOwnershipFailure());
    }
}
function* getUpdatedOwnershipInfoSaga({ payload }) {
    try {
        const entityType = yield select(entityTypeSelector);
        let method;
        switch (entityType) {
            case GlobalEntityType.CellModel:
                method = RecipeScalingApiMethods.GetCellModel;
                break;
            case GlobalEntityType.Recipe:
                method = RecipeScalingApiMethods.GetRecipe;
                break;
            case GlobalEntityType.RsWorkspace:
                method = RecipeScalingApiMethods.GetRsWorkspace;
                break;
        }
        if (method) {
            const backendClient = BackendClient.getInstance();
            const data = yield call([backendClient, method], payload);
            yield put(getUpdatedOwnershipInfoSuccessful(data));
        }
    }
    catch (e) {
        yield call(apiErrorHandler, e, "get updated ownership info failed");
        yield put(getUpdatedOwnershipInfoFailure());
    }
}
export default function* knowledgeBaseSaga() {
    yield takeLatest(KnowledgeBaseItemActionType.CREATE, createSaga);
    yield takeLatest(KnowledgeBaseItemActionType.SAVE, saveSaga);
    yield takeLatest(KnowledgeBaseItemActionType.CANCEL, cancelSaga);
    yield takeLatest(KnowledgeBaseItemActionType.RECOVER, recoverSaga);
    yield takeLatest(KnowledgeBaseItemActionType.LINK_TAGS, linkTagsSaga);
    yield takeLatest(KnowledgeBaseItemActionType.UNLINK_TAGS, unlinkTagsSaga);
    yield takeLatest(KnowledgeBaseItemActionType.UPDATE, updateSaga);
    yield takeEvery(KnowledgeBaseItemActionType.GET_RESTRICTIONS, getRestrictionsSaga);
    yield takeLatest(KnowledgeBaseItemActionType.DUPLICATE, duplicateSaga);
    yield takeEvery(KnowledgeBaseItemActionType.EXPORT, exportSaga);
    yield takeEvery(KnowledgeBaseItemActionType.SOCKET_SUBSCRIBE_MESSAGE, handleSubscribedMessage);
    yield takeEvery(KnowledgeBaseItemActionType.RECONNECT, reconnectSaga);
    yield takeEvery(KnowledgeBaseItemActionType.GET_DEPENDENT_WORKSPACES, getDependentWorkspacesSaga);
    yield takeEvery(KnowledgeBaseItemActionType.UPDATE_DEPENDENT_WORKSPACES, updateDependentWorkspacesSaga);
    yield takeEvery(KnowledgeBaseItemActionType.SHARE_OWNERSHIP, shareOwnershipSaga);
    yield takeEvery(KnowledgeBaseItemActionType.REVOKE_OWNERSHIP, revokeOwnershipSaga);
    yield takeEvery(KnowledgeBaseItemActionType.GET_UPDATED_OWNERSHIP_INFO, getUpdatedOwnershipInfoSaga);
    yield takeLatest(ROUTE_CHANGED, disconnectChannelSaga);
}
